/**
 * Browser-only: shrink an image data URL so its longest edge is at most
 * `maxEdge` px and re-encode it as JPEG. Claude downsizes anything over
 * ~1568px anyway, so this keeps request bodies small without losing detail
 * the model would see. GIFs (possibly animated) and anything that fails to
 * decode are returned unchanged.
 */
export async function downscaleImageDataUrl(
  dataUrl: string,
  { maxEdge = 1568, quality = 0.85 }: { maxEdge?: number; quality?: number } = {},
): Promise<{ url: string; mediaType: string }> {
  const mediaType = dataUrl.slice(5, dataUrl.indexOf(";"));
  if (!dataUrl.startsWith("data:image/") || mediaType === "image/gif") {
    return { url: dataUrl, mediaType };
  }

  try {
    const image = new Image();
    image.src = dataUrl;
    await image.decode();

    const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return { url: dataUrl, mediaType };

    // JPEG has no alpha: flatten transparent PNGs onto white, not black.
    context.fillStyle = "white";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const jpeg = canvas.toDataURL("image/jpeg", quality);
    // Small images can grow when re-encoded; keep whichever is smaller.
    return jpeg.length < dataUrl.length || scale < 1
      ? { url: jpeg, mediaType: "image/jpeg" }
      : { url: dataUrl, mediaType };
  } catch {
    return { url: dataUrl, mediaType };
  }
}
